// CLIO · REVIEW — core of REVISE. Planned / completed / missed come from today's
// orb items; unresolved = auto_schedule_log rows awaiting review (CLIO bell queue)
// and today's journal state, both already loaded by useTodayData.
import { dateKeyOf } from '../../time';
import type { OrbWidgetModel, WidgetCtx } from '../types';
import { emptyModel, openAction } from './util';

const BASE = { id: 'clio', toolId: 'clio', orderFunction: 'revise', title: 'REVIEW', deityName: 'CLIO', agentId: 'clio' } as const;

export function useClioWidget(c: WidgetCtx): OrbWidgetModel {
  const todayKey = dateKeyOf(c.now);
  const items = c.data.items.filter((i) => i.date === todayKey);
  const planned = items.length;
  const completed = items.filter((i) => i.completed).length;
  const changed = items.filter((i) => !i.completed && !!i.missed).length; // moves aren't recorded on the item
  const unresolved = c.today.openTonightCount ?? 0;
  const journal = !!c.today.journalWrittenToday;

  const [hh, mm] = (c.data.game.eveningSessionTime || '').split(':').map(Number);
  const evening = Number.isFinite(hh) && Number.isFinite(mm) ? hh * 60 + mm : null;
  const afterEvening = evening != null && c.now.getHours() * 60 + c.now.getMinutes() >= evening;
  const journalDue = afterEvening && !journal && planned > 0;
  const due = unresolved > 0 || journalDue;

  if (planned === 0 && unresolved === 0) return emptyModel(BASE, c, 'Nothing to review');

  const needs = unresolved + (journalDue ? 1 : 0);
  const summary = due ? `${needs} item${needs === 1 ? '' : 's'} need review` : 'Closed · no review due';
  const lines = [journal ? 'Journal written today' : 'Journal not written yet'];
  if (unresolved > 0) lines.push(`${unresolved} awaiting tick / reschedule`);

  return {
    ...BASE,
    status: due ? 'attention' : 'quiet',
    summary,
    metrics: [
      { label: 'Planned', value: planned },
      { label: 'Completed', value: completed, tone: completed > 0 ? 'positive' : 'neutral' },
      { label: 'Changed', value: changed, tone: changed > 0 ? 'warning' : 'neutral' },
      { label: 'Unresolved', value: unresolved, tone: unresolved > 0 ? 'warning' : 'neutral' },
    ],
    alert: due ? summary : null,
    lines,
    primaryAction: { label: 'Review', run: () => c.openTool('clio'), tone: due ? 'gold' : 'ghost' },
    secondaryActions: [openAction(c, 'clio', 'Clio')],
    signals: { active: planned > 0, alerted: due, related: planned > 0 },
  };
}
